import React from 'react';
import styled, { css } from 'styled-components';
import palette from '../../../lib/styles/palette';

// 제타트랙 설정 › 클럽 모니터링 탭 공통 모양 — AdminClubRunningTrackInfo(대시보드·실시간 데이터)와 같은 톤.

export const Card = styled.div`
  background: #fff;
  border: 1px solid #e0e4ea;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  margin-bottom: 30px;
`;

export const CardHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 24px;
  background: #f8f9fb;
  border-bottom: 1px solid #e0e4ea;
`;

export const CardTitle = styled.h2`
  margin: 0;
  color: #2c3e50;
  font-size: 1.15rem;
  font-weight: 700;
`;

export const Tools = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

export const CardBody = styled.div`
  padding: 20px 24px 24px;
  display: grid;
  gap: 20px;
  min-width: 0;
`;

export const Hint = styled.div`
  font-size: 13px;
  color: #868e96;
`;

export const Kpis = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 14px;
`;

const KpiBox = styled.div`
  border: 1px solid #e0e4ea;
  border-radius: 12px;
  padding: 16px 18px;
  display: grid;
  gap: 4px;
  .label { font-size: 13px; color: #868e96; }
  .value { font-size: 30px; font-weight: 700; line-height: 1.15; color: ${(p) => p.color || '#2c3e50'}; }
  .value small { font-size: 15px; font-weight: 600; margin-left: 2px; }
  .sub { font-size: 12px; color: #868e96; }
`;

export const Kpi = ({ label, value, unit, sub, color }) => (
  <KpiBox color={color}>
    <span className="label">{label}</span>
    <span className="value">{value}<small>{unit}</small></span>
    {sub ? <span className="sub">{sub}</span> : null}
  </KpiBox>
);

export const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid #e0e4ea;
  border-radius: 10px;
`;

export const Tbl = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: #2c3e50;
  font-variant-numeric: tabular-nums;

  th, td {
    padding: 9px 12px;
    border-bottom: 1px solid #eef0f4;
    text-align: left;
    vertical-align: middle;
    white-space: nowrap;
  }
  th { font-weight: 600; color: #868e96; background: #f8f9fb; }
  tbody tr:last-child td { border-bottom: 0; }
  td.r, th.r { text-align: right; }
  td.muted { color: #868e96; }
  td.strong { font-weight: 700; }
  tr.click { cursor: pointer; }
  tr.click:hover td { background: rgba(26, 115, 233, 0.06); }
  tr.detail td { background: #f8f9fb; white-space: normal; padding: 12px 16px; }
`;

export const Empty = styled.div`
  padding: 28px;
  text-align: center;
  color: #868e96;
  font-size: 14px;
`;

const chipColors = {
  blue: ['#1A73E9', 'rgba(26, 115, 233, 0.08)'],
  amber: ['#e8890c', 'rgba(232, 137, 12, 0.12)'],
  red: ['#e74c3c', '#fff5f5'],
  purple: ['#7b1fa2', '#f3e5f5'],
  gray: ['#868e96', '#f8f9fb'],
  green: ['#2e7d32', 'rgba(46, 125, 50, 0.1)'],
};
export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(p) => (chipColors[p.tone] || chipColors.gray)[0]};
  background: ${(p) => (chipColors[p.tone] || chipColors.gray)[1]};
  ${(p) => p.tone === 'gray' && css`border: 1px solid #e0e4ea;`}
  & + & { margin-left: 4px; }
`;

export const Seg = styled.div`
  display: inline-flex;
  border: 1px solid #e0e4ea;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  button {
    border: 0;
    background: transparent;
    padding: 6px 12px;
    font-size: 13px;
    color: #495057;
    cursor: pointer;
    font-family: inherit;
  }
  button + button { border-left: 1px solid #e0e4ea; }
  button[aria-pressed='true'] { background: ${palette.webCyan[5]}; color: #fff; font-weight: 700; }
  button:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const SegButtons = ({ options, value, onChange }) => (
  <Seg role="group">
    {options.map(([v, label]) => (
      <button type="button" key={v} aria-pressed={value === v} onClick={() => onChange(v)}>{label}</button>
    ))}
  </Seg>
);

export const SmallButton = styled.button`
  border: 1px solid #e0e4ea;
  background: #fff;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
  color: #495057;
  cursor: pointer;
  font-family: inherit;
  &:hover:not(:disabled) { border-color: ${palette.webCyan[5]}; color: ${palette.webCyan[5]}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const RangeText = styled.span`
  font-size: 13px;
  color: #868e96;
  font-variant-numeric: tabular-nums;
`;

const WhoBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  .avatar {
    width: 26px; height: 26px; border-radius: 50%; flex: none;
    display: grid; place-items: center;
    font-size: 12px; font-weight: 700;
    color: ${palette.webCyan[5]}; background: rgba(26, 115, 233, 0.08);
  }
  .main { font-weight: 700; }
  .sub { font-size: 12px; color: #868e96; }
`;
export const Who = ({ name, main, sub }) => (
  <WhoBox>
    <span className="avatar">{String(name || '?').charAt(1) || String(name || '?').charAt(0)}</span>
    <div>
      <div className="main">{main || name || '이름 없음'}</div>
      {sub ? <div className="sub">{sub}</div> : null}
    </div>
  </WhoBox>
);

export const Rank = styled.span`
  display: inline-grid;
  place-items: center;
  min-width: 26px;
  height: 26px;
  padding: 0 6px;
  border-radius: 13px;
  font-weight: 700;
  font-size: 12.5px;
  background: ${(p) => (p.n === 1 ? '#f5c542' : p.n === 2 ? '#c9d1da' : p.n === 3 ? '#e0a370' : '#f1f3f5')};
  color: ${(p) => (p.n === 1 ? '#4a3500' : p.n === 3 ? '#4a2600' : '#2c3e50')};
`;

export const ModalHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 24px;
  background: #f8f9fb;
  border-bottom: 1px solid #e0e4ea;
  h3 { margin: 0 0 6px; font-size: 1.15rem; font-weight: 700; color: #2c3e50; }
  .meta { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; font-size: 13px; color: #868e96; }
`;

export const ModalBody = styled.div`
  padding: 18px 24px 24px;
  display: grid;
  gap: 16px;
`;

export const Bar = styled.span`
  display: inline-block;
  height: 6px;
  border-radius: 3px;
  background: ${palette.webCyan[5]};
  opacity: 0.5;
  vertical-align: middle;
  margin-left: 8px;
`;

// 클럽 화면 보기 탭 — 눈에 잘 띄게: 회색 바탕 위에 탭이 폭을 나눠 쓰고, 고른 탭은 진한 파랑 + 흰 글씨.
const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px;
  margin: 20px 0 24px;
  background: #f1f3f5;
  border: 1px solid #e0e4ea;
  border-radius: 12px;
  button {
    flex: 1 1 0;
    min-width: 120px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    padding: 14px 20px;
    font-size: 16px;
    font-weight: 600;
    color: #495057;
    cursor: pointer;
    font-family: inherit;
    transition: background-color 0.15s ease, color 0.15s ease;
  }
  button:hover { background: #e3e7ec; color: #2c3e50; }
  button[aria-selected='true'] {
    background: ${palette.webCyan[5]};
    color: #fff;
    font-weight: 700;
    box-shadow: 0 2px 6px rgba(26, 115, 233, 0.3);
  }
`;

export const ZetaViewTabs = ({ tabs, value, onChange }) => (
  <TabBar role="tablist" aria-label="클럽 화면 보기">
    {tabs.map(([v, label]) => (
      <button type="button" role="tab" key={v} aria-selected={value === v} onClick={() => onChange(v)}>{label}</button>
    ))}
  </TabBar>
);

// 세로 막대 (날짜별 인원 등). onClick 을 주면 막대를 누를 수 있다.
const ColsBox = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 200px;
  padding: 8px 4px 0;
  overflow-x: auto;
  border-bottom: 1px solid #e0e4ea;
  .col { flex: 1 0 18px; max-width: 48px; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; }
  .col.click { cursor: pointer; }
  .col.click:hover .bar { opacity: 0.8; }
  .val { font-size: 11px; color: #495057; font-weight: 600; margin-bottom: 2px; }
  .bar { width: 70%; border-radius: 3px 3px 0 0; background: ${(p) => p.color || palette.webCyan[5]}; min-height: 2px; }
`;
const LabelsBox = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px 4px 0;
  overflow: hidden;
  span { flex: 1 0 18px; max-width: 48px; text-align: center; font-size: 11px; color: #868e96; white-space: nowrap; }
`;
export const Columns = ({ items, color, onClick, showValues = true }) => {
  const max = Math.max(1, ...items.map((x) => x.value));
  const every = items.length <= 14 ? 1 : items.length <= 31 ? 3 : 7;
  return (
    <div>
      <ColsBox color={color}>
        {items.map((x, i) => (
          <div key={x.key} className={'col' + (onClick ? ' click' : '')} title={x.title} onClick={onClick ? () => onClick(x) : undefined}>
            {showValues && items.length <= 31 ? <span className="val">{x.value}</span> : null}
            <span className="bar" style={{ height: `${(x.value / max) * 85}%` }} />
          </div>
        ))}
      </ColsBox>
      <LabelsBox>
        {items.map((x, i) => <span key={x.key}>{i % every === 0 || i === items.length - 1 ? x.label : ''}</span>)}
      </LabelsBox>
    </div>
  );
};
